import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ShieldCheck,
  TrendingUp,
  Lightbulb,
  Radio,
  Share2,
} from "lucide-react";
import { toast } from "sonner";

const CAT_CHIP = {
  "Income Tax": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "GST": "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  "Labour Law": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Companies Act": "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  "FEMA/RBI": "bg-sky-500/15 text-sky-300 border-sky-500/30",
  "Capital Markets": "bg-violet-500/15 text-violet-300 border-violet-500/30",
  "Case Laws": "bg-rose-500/15 text-rose-300 border-rose-500/30",
  "Stock Markets": "bg-lime-500/15 text-lime-300 border-lime-500/30",
  "Mixed": "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
  if (
    currentOrigin &&
    !currentOrigin.includes("preview.emergentagent.com") &&
    !currentOrigin.includes("emergent.host")
  ) {
    return currentOrigin;
  }
  return envUrl;
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function PulsePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/pulse/${encodeURIComponent(slug)}`);
        if (!res.ok) throw new Error("not_found");
        const data = await res.json();
        if (!cancelled) setPost(data);
      } catch {
        if (!cancelled) setError("Blog not found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (post?.title) {
      document.title = `${post.title} | TaxSetu AI`;
    } else {
      document.title = "Regulatory Pulse | TaxSetu AI";
    }
  }, [post]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: post?.title || "TaxSetu Regulatory Pulse", url });
        return;
      }
    } catch {
      /* fall through */
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Top bar */}
      <div className="sticky top-0 z-30 backdrop-blur-md bg-slate-950/80 border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-300 hover:text-white text-sm font-medium"
            data-testid="pulse-page-back"
          >
            <ArrowLeft className="w-4 h-4" />
            TaxSetu AI
          </button>
          <div className="flex items-center gap-2">
            <Radio className="w-3.5 h-3.5 text-rose-300" />
            <span className="text-[10px] font-bold tracking-widest text-rose-300 uppercase">
              Regulatory Pulse
            </span>
          </div>
          <button
            onClick={share}
            className="flex items-center gap-1.5 text-slate-300 hover:text-white text-xs font-medium"
            data-testid="pulse-page-share"
          >
            <Share2 className="w-3.5 h-3.5" />
            Share
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {loading && (
          <div className="animate-pulse space-y-4">
            <div className="h-3 w-32 bg-white/5 rounded" />
            <div className="h-8 w-3/4 bg-white/5 rounded" />
            <div className="h-24 bg-white/5 rounded" />
            <div className="h-24 bg-white/5 rounded" />
            <div className="h-24 bg-white/5 rounded" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center">
            <p className="text-slate-300 mb-3">{error}</p>
            <Link to="/" className="text-cyan-300 hover:text-cyan-200 text-sm font-semibold">
              ← Back to TaxSetu AI
            </Link>
          </div>
        )}

        {!loading && post && (
          <motion.article
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span
                className={`inline-block text-[9.5px] font-bold uppercase tracking-widest border rounded-full px-2 py-0.5 ${
                  CAT_CHIP[post.category] || CAT_CHIP.Mixed
                }`}
              >
                {post.category || "Mixed"}
              </span>
              {post.status && (
                <span className="inline-block text-[9.5px] font-bold uppercase tracking-widest border rounded-full px-2 py-0.5 bg-white/[0.03] border-white/15 text-slate-200">
                  Status: {post.status}
                </span>
              )}
              <span className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">
                · {formatDate(post.published_at)}
              </span>
            </div>

            <h1
              className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-white leading-tight mb-4"
              data-testid="pulse-page-title"
            >
              {post.title}
            </h1>

            {post.notification_ref && (
              <div className="mb-8 rounded-lg border border-slate-700/60 bg-slate-900/60 px-4 py-3 text-[13px] text-slate-300">
                <span className="font-semibold text-slate-200">Official reference:</span>{" "}
                {post.notification_ref}
              </div>
            )}

            {/* Sections */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 text-cyan-300" />
                </div>
                <h2 className="text-xs font-bold tracking-widest text-cyan-300 uppercase">
                  What Changed
                </h2>
              </div>
              <p className="text-[15px] text-slate-200 leading-relaxed">{post.what_changed}</p>
            </section>

            <section className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-amber-300" />
                </div>
                <h2 className="text-xs font-bold tracking-widest text-amber-300 uppercase">
                  Impact on Taxpayers & Practitioners
                </h2>
              </div>
              <p className="text-[15px] text-slate-200 leading-relaxed">{post.impact}</p>
            </section>

            <section className="mb-10">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                  <Lightbulb className="w-4 h-4 text-emerald-300" />
                </div>
                <h2 className="text-xs font-bold tracking-widest text-emerald-300 uppercase">
                  TaxSetu AI Recommendation
                </h2>
              </div>
              <p className="text-[15px] text-slate-200 leading-relaxed">
                {post.recommendation}
              </p>
            </section>

            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-center justify-between gap-3">
              <p className="text-[11px] text-slate-500 leading-snug">
                Informational blog post by TaxSetu AI. Please verify against the applicable law before acting.
              </p>
              <Link
                to="/chat"
                className="text-[12px] font-semibold text-cyan-300 hover:text-cyan-200 whitespace-nowrap"
              >
                Ask TaxSetu AI →
              </Link>
            </div>
          </motion.article>
        )}
      </div>
    </div>
  );
}
